import { warnInDev } from "../debug";
import InputState from "../engine/inputActions/InputState";
import { RDisplay } from "../ui/RotDisplay";
import { Commands } from "./commands/commands";
import { buildState } from "./gameState";
import { Bounds } from "../engine/space/bounds";
import EntityManager from "./EntityManager";
import { Action, ActionSpeed, runAction } from "./actions/Action";
import AsyncManager from "./AsyncManager";
import { getPlayerVisibility } from "./map/Visibility";
import { createDMap } from "../lib/dMap";
import { moveIn } from "./map/pathfinding";
import { generateMap } from "./map/generation";
import { ParticleManager } from "./anims/particleSystem";
import { endOfTurnEffects } from "./actors/systems/Regen";
import { clearLog, log } from "../engine/logger/logger";
import Colors from "./Colors";

export let gameData = buildState();

export const initializeState = () => {
  clearLog();
  log(
    `%c{${Colors.B_GREY_3}}Welcome to Pieux move with wasd, hjkl, or arrow keys.`
  );
  log(`%c{${Colors.B_GREY_3}}Shift + movement keys will sprint if available.`);
  log("As you awaken, the world has shifted around you");
  log(
    `With your flask of restoration (%c{${Colors.RED_4}}u%c{}), you set out.`
  );
  gameData = buildState();
  InputState.pushKeyHandler([
    "baseActions",
    "playerActions",
    "graphMovement",
    "advance",
    "cancel",
  ]);
  InputState.pushInputHandler(["base", "move", "playerActions"]);

  const rng = gameData.seedRng.getRNG();
  generateMap(rng, gameData.current);
  getPlayerVisibility(
    gameData.current.width,
    gameData.player.x,
    gameData.player.y,
    7,
    gameData.fov
  );
  gameData.dMapPlayer = createDMap([gameData.player], moveIn, 20);
  EntityManager.actorPlan();
};

const mapArrows = (key: string) => {
  return key.startsWith("Arrow") ? `S${key}` : key;
};

export const handleInput = (key: string, shiftKey: boolean) => {
  if (shiftKey) {
    key = mapArrows(key);
  }

  if (!gameData.actionPending) {
    const actionResult = InputState.keyToAction(key);
    if (actionResult) {
      const [actionName, args] = actionResult;
      const result = Commands[actionName](args);
      if (result) {
        if (Array.isArray(result)) {
          result.forEach((a) => {
            switch (a.speed) {
              case ActionSpeed.FAST:
                gameData.quickActionQueue.unshift(a);
                break;
              case ActionSpeed.MEDIUM:
                gameData.actionQueue.unshift(a);
                break;
              case ActionSpeed.SLOW:
                gameData.slowActionQueue.unshift(a);
                break;
            }
          });
        } else {
          switch (result.speed) {
            case ActionSpeed.FAST:
              gameData.quickActionQueue.unshift(result);
              break;
            case ActionSpeed.MEDIUM:
              gameData.actionQueue.unshift(result);
              break;
            case ActionSpeed.SLOW:
              gameData.slowActionQueue.unshift(result);
              break;
          }
        }
        advanceTurn();
      }
      return true;
    } else {
      warnInDev(`No action for input: ${key}`);
    }
  }
  return false;
};

export const runPlayerAction = (action: Action<any>) => {
  switch (action.speed) {
    case ActionSpeed.FAST:
      gameData.quickActionQueue.unshift(action);
      break;
    case ActionSpeed.MEDIUM:
      gameData.actionQueue.unshift(action);
      break;
    case ActionSpeed.SLOW:
      gameData.slowActionQueue.unshift(action);
      break;
  }
  advanceTurn();
};

export const advanceTurn = async () => {
  ParticleManager.clearStartOfTurn();
  gameData.actionPending = true;
  await runActionQueue();
  getPlayerVisibility(
    gameData.current.width,
    gameData.player.x,
    gameData.player.y,
    5,
    gameData.fov
  );
  gameData.dMapPlayer = createDMap([gameData.player], moveIn, 20);
  ParticleManager.clearEndOfTurn();
  endOfTurnEffects();
  EntityManager.actorPlan();
  gameData.actionPending = false;
};

export const runActionQueue = async () => {
  while (gameData.quickActionQueue.length) {
    await runAction(gameData.quickActionQueue.shift() as Action<any>);
  }
  while (gameData.actionQueue.length) {
    await runAction(gameData.actionQueue.shift() as Action<any>);
  }
  while (gameData.slowActionQueue.length) {
    await runAction(gameData.slowActionQueue.shift() as Action<any>);
  }
  await AsyncManager.waitForPending();
};

export const drawWorld = (display: RDisplay, bounds: Bounds, view: Bounds) => {
  gameData.current.draw(display, bounds, view, gameData.fov);
  gameData.gibs.forEach((particle) => {
    particle.render(display, bounds, view);
  });
  EntityManager.drawEntities(display, bounds, view);
  gameData.particles.forEach((particle) => {
    particle.render(display, bounds, view);
  });
};

export const findActorForPosition = (x: number, y: number) => {
  return gameData.actors.find((actor) => actor.x === x && actor.y === y);
};

export const getItemsForPosition = (x: number, y: number) => {
  return gameData.items.filter((item) => item.x === x && item.y === y);
};
